import { useCallback } from "react"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { serializeFusionError } from '@fusion-advantage/backcompat'
import { DashboardLoadItemStatus, DashboardSelectionType, proposeWorkItemJoinVideo, RootRouteContentType, WorkItemAction, workItemTitle, WorkItemType } from "@fusion-advantage/formlib"
import { V2L3WorkItemNotReadyDetail, V2L3WorkItemCancelledDetail, V2L3WorkItemVoidedDetail } from "@fusion-advantage/formrender"
import { LxDashboardTodayDetail } from "./LxDashboardTodayDetail"
import { LxTodoReadyDetail } from "./LxTodoReadyDetail"
import { LxApptReadyDetail } from "./LxApptReadyDetail"
import { appError } from "../../../redux/errorSlice"
import { AppModal, pushAppModal, setAppContent } from "../../../redux/appSlice"
import { updateRenderState } from "../../../lib/relay/updateRenderState"
import { CodeNext } from "@fusion-advantage/backcompat"
import { splode } from "../../../redux/thunk/splode"
import { selectSelectedStatusedWorkItem } from "../../../redux/selectSelectedWorkItem"
import { Capacitor } from "@capacitor/core"
import { apiDashboardApptSendReminder, apiDashboardGenerateClinicianVirtualJoinUrl } from "../../../redux/apithunk/apiDashboard"
import { incrementDashboardSentReminders, pushDashboardJoinedVideoEncounter, setDashboardVideoBobJobberOpen } from "../../../redux/dashboardControlSlice"
import { InAppBrowser } from '@capacitor/inappbrowser'

export const LxDashboardDetail = () => {
	const filterByDate = useAppSelector(state => state.dashboardData.filterByDate)
	const selection = useAppSelector(state => state.dashboardControl.selection)
	const selectedStatusedItem = useAppSelector(selectSelectedStatusedWorkItem)
	const displayExp = useAppSelector(state => state.dashboardMwResult.displayExp)
	const pendingSync = useAppSelector(state => state.dashboardMwResult.pendingSync)
	const videoBobJobberOpen = useAppSelector(state => state.dashboardControl.videoBobJobberOpen)
	const isTrainingMode = useAppSelector(state => state.app.isTrainingMode)
	const dispatch = useAppDispatch()
	const handleActionClick = useCallback(async (action: WorkItemAction) => {
		dispatch(updateRenderState({t1NavOpen: false}))
		if(selectedStatusedItem?.status !== DashboardLoadItemStatus.Ready) {
			dispatch(appError({
				err: serializeFusionError('LxDashboardDetail', `expected ready work item for action but null or not ready`)
			}))
			return
		}
		if(!displayExp) {
			dispatch(appError({
				err: serializeFusionError('LxDashboardDetail', `expected export for action but null`)
			}))
			return
		}
		switch(action) {
			case WorkItemAction.CancelAppointment:
				dispatch(pushAppModal(AppModal.CancelEncounter))
				break
			case WorkItemAction.StartEncounter:
			case WorkItemAction.ContinueForm:
				if(!videoBobJobberOpen && proposeWorkItemJoinVideo(selectedStatusedItem.item, displayExp, filterByDate).isOk()) {
					dispatch(setDashboardVideoBobJobberOpen(true))
				} else {
					dispatch(setAppContent({
						type: RootRouteContentType.Form,
						encounterGuid: displayExp.encounterGuid,
						action,
						workItem: selectedStatusedItem.item,
					}))
				}
				break
			case WorkItemAction.ViewForm:
				dispatch(setAppContent({
					type: RootRouteContentType.Form,
					encounterGuid: displayExp.encounterGuid,
					action,
					workItem: selectedStatusedItem.item,
				}))
				break
			case WorkItemAction.JoinVideo:
				if(isTrainingMode) {
					alert('Sorry, cannot join video call in training mode.')
					return
				}
				if(selectedStatusedItem.type !== WorkItemType.Appointment) {
					dispatch(appError({
						err: serializeFusionError('LxDashboardDetail', `expected appointment for '${action}'`)
					}))
					return
				}
				const workItem = selectedStatusedItem.item
				if(!workItem.MemberGUID) {
					dispatch(appError({
						err: serializeFusionError('LxDashboardDetail', `expected MemberGUID for '${action}' but null`)
					}))
					return
				}
				if(!workItem.ServiceAppointmentID) {
					dispatch(appError({
						err: serializeFusionError('LxDashboardDetail', `expected ServiceAppointmentID for '${action}' but null`)
					}))
					return
				}
				if(!workItem.IsJoinable) {
					alert(`Salesforce appointment is not in joinable status ('${workItem.Status}'); please contact support.`)
					return
				}
				const rGenerate = await dispatch(apiDashboardGenerateClinicianVirtualJoinUrl({
					ServiceAppointmentId: workItem.ServiceAppointmentID,
					EncounterGuid: selectedStatusedItem.encounterGuid,
				}))
				// if error, message will have already displayed
				if(rGenerate.isOk()) {
					const joinVideoUrl = rGenerate.value.ClinicianJoinUrl
					if (Capacitor.isNativePlatform()) {
						try {
							await InAppBrowser.openInExternalBrowser({url: joinVideoUrl})  // Opens in external system browser on native platforms
						} catch(e) {
							alert(e)
						}
					} else {
						window.open(joinVideoUrl, '_blank', 'noopener,noreferrer')  // Opens new tab/window
					}
					dispatch(pushDashboardJoinedVideoEncounter({encounterGuid: selectedStatusedItem.encounterGuid}))
				}
				break
			case WorkItemAction.SendReminder:
				if(selectedStatusedItem.type !== WorkItemType.Appointment) {
					dispatch(appError({
						err: serializeFusionError('LxDashboardDetail', `expected appointment for '${action}'`)
					}))
					return
				}
				const apptId = selectedStatusedItem.item.ServiceAppointmentID
				if(!apptId) {
					dispatch(appError({
						err: serializeFusionError('LxDashboardDetail', `expected service appointment id for video visit but null`)
					}))
					return
				}
				const rSend = await dispatch(apiDashboardApptSendReminder(apptId))
				// if error, message will have already displayed
				if(rSend.isOk()) {
					dispatch(incrementDashboardSentReminders({encounterGuid: selectedStatusedItem.encounterGuid}))
				}
				break
		}
	}, [filterByDate, selectedStatusedItem, displayExp, videoBobJobberOpen, isTrainingMode])
	if(selection.type === DashboardSelectionType.TodaySummary) {
		return (
			<LxDashboardTodayDetail />
		)
	}
	if(!selectedStatusedItem) {
		// we allow this to be null in the instant during dashboardMw
		// updateDashboardDetailResult while the export is loading
		// to avoid having a momentarily 'expected not null' error flash on screen
		return null
	}
	if(selectedStatusedItem.status === DashboardLoadItemStatus.Error) {
		return (
			<V2L3WorkItemNotReadyDetail
				title={selectedStatusedItem.error.name}
				error={selectedStatusedItem.error}
			/>
		)
	}
	if(!displayExp) {
		return (
			<V2L3WorkItemNotReadyDetail
				title="Error"
				error={serializeFusionError('LxDashboardDetail', 'Expected display export but null; please contact support.')}
			/>
		)
	}
	if(displayExp.codeNext === CodeNext.Cancelled) {
		return (
			<V2L3WorkItemCancelledDetail
				title={workItemTitle(selectedStatusedItem.item)}
			/>
		)
	}
	if(displayExp.codeNext === CodeNext.Voided) {
		return (
			<V2L3WorkItemVoidedDetail
				title={workItemTitle(selectedStatusedItem.item)}
				onRefreshClick={() => dispatch(splode({localOnly: true}))}
			/>
		)
	}
	if(selectedStatusedItem.item.WorkItemType === WorkItemType.Todo) {
		return (
			<LxTodoReadyDetail
				todo={selectedStatusedItem.item}
				displayExp={displayExp}
				pendingSync={pendingSync}
				onActionClick={handleActionClick}
			/>
		)
	}
	if(selectedStatusedItem.item.WorkItemType === WorkItemType.Appointment) {
		return (
			<LxApptReadyDetail
				appt={selectedStatusedItem.item}
				displayExp={displayExp}
				pendingSync={pendingSync}
				onActionClick={handleActionClick}
			/>
		)
	}
}
